import type { FormEvent } from 'react';
import { useCallback, useId } from 'react';

import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { PHONE_OTP_CODE_DIGIT_COUNT, usePhoneOtp } from '@lotushealth/web_kit/auth';

import { Button } from '@/shared/ui/shadcn/button';
import { FieldError, FieldLabel } from '@/shared/ui/shadcn/field';
import { LotusLoader } from '@/shared/ui/primitives/lotus-loader';
import { Input } from '@/shared/ui/shadcn/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/shared/ui/shadcn/input-otp';
import { auth, logger } from '@/shared';
import { formatPhoneInput } from '@/shared/lib/formatters';

import { getRecaptchaVerifier, resetRecaptchaVerifier } from '../index.js';

export interface PhoneOtpLoginProps {
  title?: string;
  className?: string;
}

export function PhoneOtpLogin({ title = 'Sign in', className }: PhoneOtpLoginProps) {
  const phoneId = useId();
  const otpId = useId();

  const onError = useCallback((stage: 'send' | 'verify', error: unknown) => {
    logger.error(`otp ${stage} failed`, { source: 'features/auth/phone-otp' }, error);
  }, []);

  const phoneOtp = usePhoneOtp({
    auth,
    getRecaptchaVerifier,
    resetRecaptchaVerifier,
    onError,
    formatPhoneInput,
  });

  const isSending = phoneOtp.busy === 'sending';
  const isVerifying = phoneOtp.busy === 'verifying';

  const onSend = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      await phoneOtp.sendCode();
    },
    [phoneOtp]
  );

  const onVerify = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      await phoneOtp.verifyCode();
    },
    [phoneOtp]
  );

  const heading = phoneOtp.stage === 'verify' ? 'Enter verification code' : title;
  const subtitle =
    phoneOtp.stage === 'verify'
      ? `We just sent a ${PHONE_OTP_CODE_DIGIT_COUNT} digit verification code`
      : 'Use your phone number to receive a verification code.';

  return (
    <div className={`flex w-full flex-col gap-[22px] ${className ?? ''}`}>
      <h1 className="text-2xl font-medium">{heading}</h1>
      <p className="text-muted-foreground text-sm">{subtitle}</p>

      {phoneOtp.stage === 'phone' ? (
        <form onSubmit={onSend} className="flex w-full flex-col gap-[22px]">
          <FieldLabel htmlFor={phoneId} className="sr-only">
            Phone number
          </FieldLabel>
          <Input
            id={phoneId}
            type="tel"
            inputSize="lg"
            radius="default"
            value={phoneOtp.phone}
            onChange={(e) => phoneOtp.setPhoneInput(e.currentTarget.value)}
            autoComplete="tel"
            inputMode="tel"
            placeholder="(555) 555-5555"
            aria-invalid={phoneOtp.errorMessage ? true : undefined}
            disabled={phoneOtp.isBusy}
            required
            className="rounded-none border-x-0 border-t-0 border-b border-b-border/80 px-0 bg-transparent shadow-none focus-visible:border-primary focus-visible:ring-0 dark:border-b-white/40 dark:bg-transparent"
          />
          <FieldError>{phoneOtp.errorMessage}</FieldError>

          <div className="relative w-full">
            <Button
              type="submit"
              radius="pill"
              size="lg"
              className="w-full"
              disabled={phoneOtp.isBusy || !phoneOtp.isPhoneComplete}
            >
              <span className={isSending ? 'opacity-0' : undefined}>Send code</span>
            </Button>
            {isSending && (
              <LotusLoader size={28} className="pointer-events-none absolute inset-0 m-auto" />
            )}
          </div>
        </form>
      ) : (
        <form onSubmit={onVerify} className="flex w-full flex-col gap-[22px]">
          <FieldLabel htmlFor={otpId} className="sr-only">
            Verification code
          </FieldLabel>
          <InputOTP
            id={otpId}
            value={phoneOtp.code}
            onChange={phoneOtp.setCodeInput}
            maxLength={PHONE_OTP_CODE_DIGIT_COUNT}
            pattern={REGEXP_ONLY_DIGITS}
            autoComplete="one-time-code"
            inputMode="numeric"
            containerClassName="justify-start"
            aria-invalid={phoneOtp.errorMessage ? true : undefined}
            disabled={phoneOtp.isBusy}
            required
            autoFocus
          >
            <InputOTPGroup className="max-w-full gap-2.5 *:data-[slot=input-otp-slot]:size-12 *:data-[slot=input-otp-slot]:rounded-none *:data-[slot=input-otp-slot]:border-x-0 *:data-[slot=input-otp-slot]:border-t-0 *:data-[slot=input-otp-slot]:border-b *:data-[slot=input-otp-slot]:border-b-border/80 *:data-[slot=input-otp-slot]:bg-transparent *:data-[slot=input-otp-slot]:shadow-none *:data-[slot=input-otp-slot]:data-[active=true]:border-primary *:data-[slot=input-otp-slot]:data-[active=true]:ring-0 dark:*:data-[slot=input-otp-slot]:border-b-white/40 dark:*:data-[slot=input-otp-slot]:bg-transparent">
              {Array.from({ length: PHONE_OTP_CODE_DIGIT_COUNT }, (_, index) => (
                <InputOTPSlot key={index} index={index} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <FieldError className="text-left">{phoneOtp.errorMessage}</FieldError>

          <div className="relative w-full">
            <Button
              type="submit"
              radius="pill"
              size="lg"
              className="w-full"
              disabled={phoneOtp.isBusy || phoneOtp.code.length !== PHONE_OTP_CODE_DIGIT_COUNT}
            >
              <span className={isVerifying ? 'opacity-0' : undefined}>Verify</span>
            </Button>
            {isVerifying && (
              <LotusLoader size={28} className="pointer-events-none absolute inset-0 m-auto" />
            )}
          </div>
        </form>
      )}
    </div>
  );
}
